import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Components
import {
  Avatar,
  Button,
  Col,
  Input,
  Row,
  ShoutoutPresets,
  Text,
  useShoutoutPresets
} from 'components';

// Utils
import { Api, theme } from 'lib';
import { SHOUTOUT_SOURCE_CTA } from '../constants';

/*
  A preset and a custom message are mutually exclusive, but the composer stays
  live either way — focusing it is what clears the selected preset, so this
  wrapper exists to catch the bubbled focus event from the textarea.
*/
const ComposerSection = styled.div`
  width: 100%;
`;

/*
  Compose step of the CTA flow: pick one of the preset messages (fetched from
  the shoutoutPresets collection) or write a custom message.
*/
const CtaPostShoutout = ({ basePath, otherUser }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { presets, fallback, loading, refetch, getPresetText } = useShoutoutPresets({
    teamId: otherUser?._id,
    teamName: otherUser?.name
  });

  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [comment, setComment] = useState('');

  function handlePresetSelect(preset) {
    // the preset takes over as the message, so drop any custom draft
    if (preset) setComment('');
    setSelectedPresetId(preset?._id || null);
  }

  // focusing the composer is the other half of the toggle: it drops the preset
  function handleComposerFocus() {
    setSelectedPresetId(null);
  }

  const selectedPreset = presets.find((x) => x._id === selectedPresetId);
  const message = selectedPreset ? getPresetText(selectedPreset) : comment.trim();
  const disabled = !message;

  async function handlePost() {
    const res = await Api.post(
      '/shoutouts/create',
      {
        comment: message,
        entityId: otherUser?._id,
        // marks the CTA flow — the backend auto-follows the team only for this source
        source: SHOUTOUT_SOURCE_CTA,
        // presetId lets the backend enforce the per-user preset cooldown
        ...(selectedPresetId && { presetId: selectedPresetId })
      },
      'postShoutout',
      // silent so coded errors (PRESET_COOLDOWN etc.) can be handled inline
      { silent: true }
    );
    if (!res) return;

    // the preset went on cooldown between fetch and post (e.g. second tab) —
    // clear the stale selection and resync the pills with the server
    if (res.code === 'PRESET_COOLDOWN' || res.code === 'PRESET_LIMIT') {
      dispatch({ type: 'HANDLE_RES', payload: { err: t('callToAction.presetUnavailable') } });
      setSelectedPresetId(null);
      refetch();
      return;
    }

    // any other failure: reproduce the global toast the non-silent path shows.
    // coded errors (e.g. 429 LIMIT_EXCEEDED) carry their user-facing text in
    // `message` instead of `err` — show that instead of the generic fallback
    if (res.err || res.status) {
      const backendMessage = res.err || (res.code && res.message);
      const errMessage =
        res.status >= 500 || !backendMessage
          ? t('error.somethingWentWrong') + '\n' + t('error.pleaseTryAgainLater')
          : backendMessage;
      dispatch({ type: 'HANDLE_RES', payload: { err: errMessage } });
      return;
    }

    // replace so browser back from the success screen skips the spent composer;
    // points earned (from the create response) shown on the success screen.
    // celebrate marks a fresh arrival — the success screen clears it so a
    // refresh doesn't replay the confetti
    navigate(`${basePath}/success`, {
      replace: true,
      state: { points: res.points, shoutoutUsage: res.shoutoutUsage, celebrate: true }
    });
  }

  return (
    <Col wid="100%" centerAll noFlex>
      {/* who the shout-out is for */}
      <Row centerAll noFlex>
        <Avatar user={otherUser} showVerified />

        <Col marg="0 0 0 12px" center>
          <Text h2 col="#fff">
            {otherUser?.name}
          </Text>

          <Text c1 med col="#fff">
            @{otherUser?.username}
          </Text>
        </Col>
      </Row>

      {/* wrapper is needed because the parent Col centers its children — the
          full-width Row is what lets the heading sit flush left. `start` is
          required too: Row's justify-content defaults to `inherit`, which would
          pick up the parent's centering. */}
      {/* same size as the "write your own message" label below — the two are
          peer section headings */}
      <Row start wid="100%" noFlex marg="24px 0 0">
        <Text h4 col="#fff">
          {t('callToAction.yourShoutoutTo', { name: otherUser?.name })}
        </Text>
      </Row>

      {/* preset messages — tap to select, tap again to write your own */}
      <Col wid="100%" marg="8px 0 0" noFlex>
        <ShoutoutPresets
          dark
          presets={presets}
          fallback={fallback}
          loading={loading}
          getPresetText={getPresetText}
          selectedPresetId={selectedPresetId}
          onSelect={handlePresetSelect}
          teamName={otherUser?.name}
        />
      </Col>

      <Col wid="100%" marg="24px 0 0" noFlex>
        <Text h4 col="#fff">
          {t('callToAction.writeYourOwnMessage')}
        </Text>
      </Col>

      {/* onFocus bubbles in React, so this catches focus from the textarea */}
      <ComposerSection onFocus={handleComposerFocus}>
        <Input
          marg="8px 0 0"
          wid="100%"
          bg="#fff"
          multiline
          emoji
          ht="100px"
          placeholder={t('callToAction.typeMessageOrEmojis')}
          value={comment}
          onChange={(e) => {
            setSelectedPresetId(null);
            setComment(e.target.value);
          }}
        />
      </ComposerSection>

      <Button
        big
        wid="100%"
        marg="16px 0 0"
        bg={disabled ? theme.GREY_30 : theme.SECONDARY}
        text={t('callToAction.postShoutout')}
        onClick={disabled ? undefined : handlePost}
        action="postShoutout"
      />
    </Col>
  );
};

export default CtaPostShoutout;
